import { createSelector } from "@reduxjs/toolkit";

import { selectThemeItem } from "@/features/app/appSlice";
import { selectConfigItem } from "@/features/config/configSlice";
import {
  selectMapViewLimits,
  selectMapViewState,
} from "@/features/map/mapSlice";

import type { RootState } from "@/app/store";
import {
  selectCurrentBuilding,
  selectCurrentFloorID,
} from "@/features/cartography/cartographySlice.selectors";
import { MAX_ZOOM } from "@/features/map/constants";
import {
  computeCurrentSegments,
  computeFloorChanges,
  computeRouteLine,
} from "@/features/utils/routes";
import {
  selectNavigationFrom,
  selectNavigationTo,
} from "../navigation/navigationSlice.selectors";
import { getPoiIdFromIndicationMetaData } from "../utils/navigation";
import { DIRECTIONS_ERROR_CODES, RouteComputationStatus } from "./types";

// ----------------
// General selectors

const sliceData = (state: RootState) => state.directions;

export const selectDirectionsRoutes = createSelector(sliceData, (item) => {
  return item.routes;
});

export const selectRouteComputationStatus = createSelector(
  sliceData,
  (item) => {
    return item.routeComputationStatus;
  },
);

export const selectSelectedRouteId = createSelector(sliceData, (item) => {
  return item.selectedRouteId;
});

export const selectSelectedRouteType = createSelector(sliceData, (item) => {
  return item.selectedRouteType;
});

export const selectRouteComputationFinished = createSelector(
  sliceData,
  (item) => {
    return item.routeComputationStatus === RouteComputationStatus.FINISHED;
  },
);

export const selectSelectedRoute = createSelector(sliceData, (item) => {
  return item.routes?.find((route) => route.id === item.selectedRouteId);
});

export const selectDirectionsSearchModeEnabled = createSelector(
  sliceData,
  (item) => {
    return item.search.enabled;
  },
);

export const selectDirectionsShowingMap = createSelector(sliceData, (item) => {
  return item.search.showingMap;
});

export const selectDirectionsSelectorNameSelected = createSelector(
  sliceData,
  (item) => {
    return item.search.selectorNameSelected;
  },
);

export const selectCurrentDirectionsRoutePOIs = createSelector(
  sliceData,
  (item) => {
    return item.routes
      ?.find((x) => x.id === item.selectedRouteId)
      ?.indications.reduce(
        (acc, x) => {
          if (x.metaData) {
            acc = [...acc, getPoiIdFromIndicationMetaData(x.metaData)];
          }
          return acc;
        },
        [] as (number | null | undefined)[],
      )
      .filter(Boolean);
  },
);

export const selectDirectionsErrors = createSelector(sliceData, (item) => {
  return item.errors;
});

export const selectHasOutdoorOobDirectionsError = createSelector(
  selectDirectionsErrors,
  (errors) =>
    errors?.includes(DIRECTIONS_ERROR_CODES.outdoor_oob_location) ?? false,
);

export const selectIsAutoPlaying = createSelector(sliceData, (item) => {
  return item.isAutoPlaying;
});

// ----------------
// Route processing

export const selectDirectionsSegments = createSelector(
  [selectDirectionsRoutes, selectCurrentBuilding, selectCurrentFloorID],
  (routes, currentBuilding, currentFloorID) => {
    if (!routes) return [];

    return routes.map((route) =>
      computeCurrentSegments(currentBuilding, route, currentFloorID),
    );
  },
);

const selectUnfilteredDirectionsSegments = createSelector(
  [selectDirectionsRoutes, selectCurrentBuilding, selectCurrentFloorID],
  (routes, currentBuilding, currentFloorID) => {
    if (!routes) {
      return [];
    }
    return routes.map((route) =>
      computeCurrentSegments(
        currentBuilding,
        route,
        currentFloorID,
        false,
        false,
      ),
    );
  },
);

export const selectDirectionPaths = createSelector(
  [
    selectDirectionsRoutes,
    selectCurrentBuilding,
    selectDirectionsSegments,
    (state) => selectMapViewState(state).zoom,
    (state) => selectMapViewLimits(state).maxZoom,
    (state) => selectThemeItem("route.pathDotGap")(state),
    (state) => selectConfigItem("internal.embed")(state),
  ],
  (routes, currentBuilding, segments, zoom, maxZoom, pathDotGap, modeEmbed) => {
    if (!routes || !currentBuilding) return [];

    return segments.map((routeSegment, idx) =>
      computeRouteLine({
        customOffset: pathDotGap,
        isEmbed: modeEmbed,
        routeId: routes[idx].id,
        segments: routeSegment,
        zoom: Math.min(zoom, maxZoom || MAX_ZOOM),
      }),
    );
  },
);

const selectCurrentRouteIndex = createSelector(
  [
    selectSelectedRouteId,
    selectDirectionsRoutes,
    selectDirectionsSegments,
    selectDirectionPaths,
    selectRouteComputationFinished,
    selectNavigationTo,
    selectNavigationFrom,
  ],
  (
    selectedRouteId,
    routes,
    segments,
    paths,
    didRouteComputationFinish,
    navigationTo,
    navigationFrom,
  ) => {
    if (
      !routes ||
      !segments ||
      !paths ||
      selectedRouteId === undefined ||
      selectedRouteId < 0 ||
      !didRouteComputationFinish
    ) {
      return {};
    }

    const currentRouteIdx = routes?.findIndex(
      (r) =>
        r.destination === navigationTo &&
        r.origin === navigationFrom &&
        r.id === selectedRouteId,
    );

    if (currentRouteIdx === undefined || currentRouteIdx < 0) {
      return;
    }

    return currentRouteIdx;
  },
);

export const selectCurrentDirectionsRoute = createSelector(
  [selectDirectionsRoutes, selectCurrentRouteIndex],
  (routes, currentRouteIndex) => {
    if (currentRouteIndex === undefined) {
      return;
    }

    return routes[currentRouteIndex];
  },
);

export const selectCurrentDirectionsSegments = createSelector(
  [selectDirectionsSegments, selectCurrentRouteIndex],
  (segments, currentRouteIndex) => {
    if (currentRouteIndex === undefined) {
      return;
    }

    return segments[currentRouteIndex];
  },
);

export const selectCurrentDirectionsPath = createSelector(
  [selectDirectionPaths, selectCurrentRouteIndex],
  (paths, currentRouteIndex) => {
    if (currentRouteIndex === undefined) {
      return;
    }

    return paths[currentRouteIndex];
  },
);

export const selectCurrentDirectionsStep = createSelector(
  [selectCurrentDirectionsRoute],
  (route) => {
    return route?.currentStep;
  },
);

// Obtain a list of floor changes, and the origin-destination mapping
export const selectDirectionsFloorChanges = createSelector(
  [
    selectCurrentBuilding,
    selectDirectionsRoutes,
    selectCurrentDirectionsRoute,
    selectUnfilteredDirectionsSegments,
    selectCurrentDirectionsStep,
  ],
  (currentBuilding, routes, currentRoute, unfilteredSegments) => {
    return routes?.map((route, idx) =>
      computeFloorChanges({
        building: currentBuilding,
        isSelectedRoute: route.id === currentRoute?.id,
        routeId: route.id,
        segments: unfilteredSegments[idx],
        steps: route.steps,
      }),
    );
  },
);
